import "./App.css";

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import React, { lazy, Suspense, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, Route, Routes } from "react-router-dom";

import DashboardSkeleton from "./components/common/DashboardLoader";
// Regular import for Login (no lazy loading)
import Login from "./pages/auth/Login";
import Reports from "./pages/reports/Reports";
// Other imports
import { fetchProfile } from "./redux/actions/actions";
import { setProfileSliceData } from "./redux/features/profile/profileSlice";
import { setOrganisation } from "./redux/features/sidebar/sidebarSlice";
import ProtectedRoute from "./routes/ProtectedRoute";
import RequireAuth from "./routes/RequireAuth";
import RequireLogin from "./routes/RequireLogin";
import SurveyQuestionDetailsPage from "pages/survey/questions/details";
import SurveyAssignmentDetailsPage from "pages/survey/assignment/details";
import SensorAccessControl from "./components/sensor/SensorAccessControl";
// Register necessary components with Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

import ErrorPage from "./pages/error/Error";
import UserBulkUploadEdit from "pages/users/userBulkUploadEdit";
import CopyPasteProtection from "components/common/CopyPasteProtection";
import UserGroupBulkUploadEdit from "pages/users/groups/userGroupBulkUploadEdit";
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const TwoFAScreens = lazy(() => import("./pages/auth/TwoFAScreens"));
const BillingOverview = lazy(() => import("./pages/billing/overview"));
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const IntegratedSettings = lazy(
  () => import("./pages/sensors/IntegratedSettings.jsx")
);
const FeedArticlesCreate = lazy(() => import("./pages/feed/articles/create"));
const FeedArticlesEdit = lazy(() => import("./pages/feed/articles/edit"));
const FeedArticlesList = lazy(() => import("./pages/feed/articles/list"));
const ConditionComplianceDetails = lazy(
  () =>
    import(
      "./pages/sensors/meat/HccCategoryReport/hccConditionComplianceReport"
    )
);
const FeedContentAssignment = lazy(
  () => import("./pages/feed/assignment/list")
);
const FeedContentAssignmentDetail = lazy(
  () => import("./pages/feed/assignment/detail")
);
const FeedAssignContent = lazy(
  () => import("./pages/feed/assign-content/assignContent")
);
const UserGroupBulkUpload = lazy(
  () => import("./pages/users/groups/bulkUpload")
);
const FeedVOWCreate = lazy(() => import("./pages/feed/vow/create"));
const FeedVOWEdit = lazy(() => import("./pages/feed/vow/edit"));
const FeedVOWList = lazy(() => import("./pages/feed/vow/list"));
const PearlsAndTipsList = lazy(() => import("./pages/feed/pearlsAndTips/list"));
const PearlsAndTipsCreatePage = lazy(
  () => import("./pages/feed/pearlsAndTips/create")
);
const PearlsAndTipsEditPage = lazy(
  () => import("./pages/feed/pearlsAndTips/edit")
);
const Files = lazy(() => import("./pages/files/Files"));
const ProgramsCreate = lazy(() => import("./pages/programs/create"));
const ProgramPreview = lazy(() => import("./pages/programs/preview"));
const ProgramsEdit = lazy(() => import("./pages/programs/edit"));
const ProgramsList = lazy(() => import("./pages/programs/list"));
const ProgramsModulesCreate = lazy(
  () => import("./pages/programs/modules/create")
);
const ProgramsModulesPreview = lazy(
  () => import("./pages/programs/modules/preview")
);
const ProgramsModulesEdit = lazy(() => import("./pages/programs/modules/edit"));
const ProgramsModulesList = lazy(() => import("./pages/programs/modules/list"));
const ProgramsQuestionsCreate = lazy(
  () => import("./pages/programs/questions/create")
);
const ContradictionDashboard = lazy(
  () => import("./pages/sensors/hcc-pe-contradiction/contradictionDashboard")
);
const ProgramAssignmentlist = lazy(
  () => import("./pages/programs/assignment/list")
);
const AssignmentPreview = lazy(
  () => import("./pages/programs/assignment/preview")
);
const ProgramAssignment = lazy(
  () => import("./pages/programs/assignment/ProgramAssignment")
);
const ProgramsQuestionsDetails = lazy(
  () => import("./pages/programs/questions/details")
);
const ProgramQuestionsAssociations = lazy(
  () => import("./pages/programs/questions/associations")
);
const ProgramsQuestionsEdit = lazy(
  () => import("./pages/programs/questions/Update")
);
const ProgramsQuestionsList = lazy(
  () => import("./pages/programs/questions/list")
);
const ProgramsQuestionsUpload = lazy(
  () => import("./pages/programs/questions/upload")
);
const HCCCateroryReport = lazy(
  () => import("./pages/sensors/meat/HccCategoryReport/hccCategoryReport")
);
const UserCompliance = lazy(() => import("./pages/reports/UserCompliance"));
const UserEngagement = lazy(() => import("./pages/reports/UserEngagement"));
const UserTrackerReport = lazy(
  () => import("./pages/reports/UserTrackerReport")
);
const Settings = lazy(() => import("./pages/settings/Settings"));
const ScorecardConfigCreate = lazy(
  () => import("./pages/targets/configurations/Create")
);
const ScorecardConfigEdit = lazy(
  () => import("./pages/targets/configurations/Edit")
);
const TargetDashboard = lazy(() => import("./pages/targets/Dashboard"));
const TargetReports = lazy(() => import("./pages/targets/Reports"));
const ContestsCreate = lazy(
  () => import("./pages/tournaments/contests/create")
);
const ContestEdit = lazy(() => import("./pages/tournaments/contests/edit"));
const ContestDetail = lazy(() => import("./pages/tournaments/contests/detail"));
const TournamentCreatePage = lazy(
  () => import("./pages/tournaments/TournamentCreatePage")
);
const TournamentDetails = lazy(() => import("./pages/tournaments/details"));
const TournamentEdit = lazy(() => import("./pages/tournaments/edit"));
const TournamentList = lazy(() => import("./pages/tournaments/list"));
const CreateNewUsers = lazy(() => import("./pages/users/CreateNewUsers"));
const UserBulkUpload = lazy(() => import("./pages/users/bulkUpload"));
const CreateNewUserGroup = lazy(() => import("./pages/users/groups/create"));
const EditNewUserGroup = lazy(() => import("./pages/users/groups/edit"));
const UserGroupsList = lazy(() => import("./pages/users/groups/list"));
const Users = lazy(() => import("./pages/users/Users"));
const ClinicalPerformance = lazy(
  () => import("./pages/targets/ClinicalPerformance")
);
const UserGroupDetail = lazy(() => import("./pages/users/groups/groupdetail"));
const ClinicLevelPerformancePage = lazy(
  () => import("./pages/targets/ClinicLevelPerformancePage")
);

const DTSensors = lazy(() => import("./pages/sensors/index"));
const MeatNonComplianceReport = lazy(
  () => import("./pages/sensors/MeatNonComplianceReport")
);

const SurveyQuestions = lazy(() => import("./pages/survey/questions/list"));
const CreateSurveyQuestion = lazy(
  () => import("./pages/survey/questions/create")
);
const EditSurveyQuestion = lazy(() => import("./pages/survey/questions/edit"));
const SurveyModules = lazy(() => import("./pages/survey/modules/list"));
const SurveyModuleDetails = lazy(
  () => import("./pages/survey/modules/details")
);
const CreateSurveyModule = lazy(() => import("./pages/survey/modules/create"));
const SurveyAssignments = lazy(() => import("./pages/survey/assignment/list"));
const ClinicianComplianceReport = lazy(
  () => import("./pages/sensors/meat/ClinicianComplianceReport")
);
const ContradictionClincianComplianceReport = lazy(
  () => import("./pages/sensors/hcc-pe-contradiction/clinicianComplianceReport")
);

const ClinitianProgressNotes = lazy(
  () => import("./pages/sensors/meat/ClinitianProgressNotes")
);
const AnalysisDetails = lazy(
  () => import("./pages/sensors/meat/AnalysisDetails.jsx")
);

const Contestlisting = lazy(
  () => import("./pages/tournaments/contests/contestlisting.jsx")
);

const TaileredContestCreatePage = lazy(
  () => import("./pages/tailored-content/TailoredContentCreatePage.jsx")
);

const LearningPointsTailoredContestCreatePage = lazy(
  () =>
    import(
      "./pages/tailored-content/leaningPointsTailoredContestCreatePage.jsx"
    )
);

const TailoredContentlisting = lazy(
  () => import("./pages/tailored-content/TailoredContentListing.jsx")
);

const TailoredContestDetailPage = lazy(
  () => import("./pages/tailored-content/TailoredContestDetailPage.jsx")
);

const CustomAssignmentQuestionListing = lazy(
  () => import("./pages/tailored-content/CustomAssignmentQuestionListing.jsx")
);

const ProgramLibrary = lazy(
  () => import("./pages/programs/program-library/programLibrary.jsx")
);

const ProgramDetail = lazy(
  () => import("./pages/programs/program-library/programDetail.jsx")
);

const HccPEProgrgressNoteListing = lazy(
  () => import("./pages/sensors/hcc-pe-contradiction/progressNoteListing.jsx")
);

const HccPeAnalysisDetails = lazy(
  () => import("./pages/sensors/hcc-pe-contradiction/progressNoteDetail.jsx")
);

const BuddyCodingDashboard = lazy(
  () => import("./pages/sensors/buddyCoding/BuddyCodingDashboard")
);

const BuddyCodingClinicianComplianceReport = lazy(
  () => import("./pages/sensors/buddyCoding/clinicianComplianceReport")
);

const BuddyCodingProgressNoteListing = lazy(
  () => import("./pages/sensors/buddyCoding/progressNoteListing")
);

const BuddyCodingAnalysisDetails = lazy(
  () => import("./pages/sensors/buddyCoding/progressNoteDetail")
);

const DiagnosticStateDashboard = lazy(
  () => import("./pages/sensors/diagnosticState/diagnoticStateDashboard")
);

const DiagnosticStateClinicianComplianceReport = lazy(
  () => import("./pages/sensors/diagnosticState/clinicianComplianceReport")
);

const DiagnosticStateProgressNoteListing = lazy(
  () => import("./pages/sensors/diagnosticState/progressNoteListing")
);

const DiagnosticStateAnalysisDetails = lazy(
  () => import("./pages/sensors/diagnosticState/progressNoteDetail")
);

const PendingSignatureDashboard = lazy(
  () => import("./pages/sensors/pendingSignatures/pendingSignatureDashboard")
);
const PendingSgnatureProgressNoteListing = lazy(
  () =>
    import(
      "./pages/sensors/pendingSignatures/pendingSignatureProgressNoteListing"
    )
);

const SensorDashboard = lazy(
  () => import("./pages/sensors/overallCompliance/overallComplianceDashboard")
);

const ClinicianProgressNoteListing = lazy(
  () => import("./pages/sensors/overallCompliance/clinicianProgressNoteListing")
);

const ClinicProviderStatsListing = lazy(
  () => import("./pages/sensors/overallCompliance/clinicProviderStatsListing")
);

const RegionProviderStatsListing = lazy(
  () => import("./pages/sensors/overallCompliance/regionProviderStatsListing")
);

const ProviderReportListing = lazy(
  () => import("components/sensor/pendingSignatures/providerReportListing")
);

const TopClinicsTable = lazy(
  () => import("./components/sensor/overallCompliance/TopClincsTable")
);

const TopRegionsTable = lazy(
  () => import("./components/sensor/overallCompliance/TopRegionsTable")
);

const TopCliniciansTable = lazy(
  () => import("./components/sensor/overallCompliance/TopCliniciansTable")
);

function App() {
  const dispatch = useDispatch();
  const userProfile = useSelector((e) => e.profile.userProfile);
  const isProfileLoading = useSelector((e) => e.profile.profileLoading);
  const isUserLoggedIn = localStorage.getItem("token");

  useEffect(() => {
    if (userProfile !== undefined || isProfileLoading) return;
    if (!isUserLoggedIn) return;
    dispatch(setProfileSliceData({ type: "profileLoading", data: true }));
    dispatch(fetchProfile())
      .then((e) => {
        dispatch(setOrganisation(e.payload.organizations[0]));
        localStorage.setItem("organization", e.payload.organizations[0]);
      })
      .catch(() => {
        localStorage.removeItem("token");
        dispatch(setProfileSliceData({ type: "userProfile", data: undefined }));
        window.location.href = "/";
      });
  }, []);

  if (isProfileLoading || (userProfile === undefined && isUserLoggedIn)) {
    return <DashboardSkeleton />;
  }

  return (
    <Routes>
      <Route path="/" element={<Outlet />}>
        {/* Public route  */}
        <Route path="/" element={<RequireLogin />}>
          <Route path="" element={<Login />} />
          <Route path="forgotpassword" element={<ForgotPassword />} />
        </Route>
        {/* protected routes */}
        <Route path="/home" element={<RequireAuth />}>
          <Route
            path=""
            element={
              <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="2FA"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <TwoFAScreens />
              </Suspense>
            }
          />
          <Route
            path="dashboard"
            element={
              <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route path="dt-sensors" element={<SensorAccessControl />}>
            <Route
              path="meat"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <CopyPasteProtection>
                    <DTSensors />
                  </CopyPasteProtection>
                </ProtectedRoute>
              }
            />
            <Route
              path="integrated-settings"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <CopyPasteProtection>
                    <IntegratedSettings />
                  </CopyPasteProtection>
                </ProtectedRoute>
              }></Route>
            <Route
              path="integrated-settings"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <IntegratedSettings />
                </ProtectedRoute>
              }></Route>
            <Route
              path="meat-non-compliance-report"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <CopyPasteProtection>
                    <MeatNonComplianceReport />
                  </CopyPasteProtection>
                </ProtectedRoute>
              }
            />
            <Route path="meat-clinician-compliance-report" element={<Outlet />}>
              <Route
                path="clinician-compliance-report"
                element={
                  <ProtectedRoute allowedRoles={["client_admin"]}>
                    <CopyPasteProtection>
                      <ClinicianComplianceReport />
                    </CopyPasteProtection>
                  </ProtectedRoute>
                }
              />
              <Route
                path="clinician-compliance-report/:ClinicianId"
                element={
                  <ProtectedRoute allowedRoles={["client_admin"]}>
                    <CopyPasteProtection>
                      <ClinitianProgressNotes />
                    </CopyPasteProtection>
                  </ProtectedRoute>
                }
              />
              <Route
                path="clinician-compliance-report/:ClinicianId/meat-analysis/:analysisId"
                element={
                  <ProtectedRoute allowedRoles={["client_admin"]}>
                    <CopyPasteProtection>
                      <AnalysisDetails />
                    </CopyPasteProtection>
                  </ProtectedRoute>
                }
              />
            </Route>
            <Route
              path="hcc-categories-report"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <HCCCateroryReport></HCCCateroryReport>
                </ProtectedRoute>
              }
            />
            <Route
              path="hcc-condition-compliance/:categoryId"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <ConditionComplianceDetails></ConditionComplianceDetails>
                </ProtectedRoute>
              }
            />
            <Route
              path="hcc-pe-contradiction"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <ContradictionDashboard></ContradictionDashboard>
                </ProtectedRoute>
              }
            />
            <Route
              path="hcc-pe-contradiction/clinician-report"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <ContradictionClincianComplianceReport></ContradictionClincianComplianceReport>
                </ProtectedRoute>
              }
            />
            <Route
              path="hcc-pe-contradiction/progress-note-listing"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <HccPEProgrgressNoteListing></HccPEProgrgressNoteListing>
                </ProtectedRoute>
              }
            />
            <Route
              path="hcc-pe-contradiction/progress-note-listing/:ClinicianId"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <HccPEProgrgressNoteListing></HccPEProgrgressNoteListing>
                </ProtectedRoute>
              }
            />
            <Route
              path="hcc-pe-contradiction/progress-note-detail/:progressNoteId"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <HccPeAnalysisDetails></HccPeAnalysisDetails>
                </ProtectedRoute>
              }
            />
            <Route
              path="buddy-coding"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <BuddyCodingDashboard></BuddyCodingDashboard>
                </ProtectedRoute>
              }
            />
            <Route
              path="buddy-coding/clinician-report"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <BuddyCodingClinicianComplianceReport></BuddyCodingClinicianComplianceReport>
                </ProtectedRoute>
              }
            />
            <Route
              path="buddy-coding/progress-note-listing"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <BuddyCodingProgressNoteListing></BuddyCodingProgressNoteListing>
                </ProtectedRoute>
              }
            />
            <Route
              path="buddy-coding/progress-note-detail/:progressNoteId"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <BuddyCodingAnalysisDetails></BuddyCodingAnalysisDetails>
                </ProtectedRoute>
              }
            />
            <Route
              path="buddy-coding/progress-note-listing/:ClinicianId"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <BuddyCodingProgressNoteListing></BuddyCodingProgressNoteListing>
                </ProtectedRoute>
              }
            />
            <Route
              path="buddy-coding/progress-note-detail/:progressNoteId"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <BuddyCodingAnalysisDetails></BuddyCodingAnalysisDetails>
                </ProtectedRoute>
              }
            />
            <Route
              path="diagnostic-state"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <DiagnosticStateDashboard></DiagnosticStateDashboard>
                </ProtectedRoute>
              }
            />
            <Route
              path="diagnostic-state/clinician-report"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <DiagnosticStateClinicianComplianceReport></DiagnosticStateClinicianComplianceReport>
                </ProtectedRoute>
              }
            />
            <Route
              path="diagnostic-state/progress-note-listing"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <DiagnosticStateProgressNoteListing></DiagnosticStateProgressNoteListing>
                </ProtectedRoute>
              }
            />
            <Route
              path="diagnostic-state/progress-note-listing/:ClinicianId"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <DiagnosticStateProgressNoteListing></DiagnosticStateProgressNoteListing>
                </ProtectedRoute>
              }
            />
            <Route
              path="diagnostic-state/progress-note-detail/:progressNoteId"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <DiagnosticStateAnalysisDetails></DiagnosticStateAnalysisDetails>
                </ProtectedRoute>
              }
            />
            <Route
              path="pending-signatures"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <PendingSignatureDashboard></PendingSignatureDashboard>
                </ProtectedRoute>
              }
            />
            <Route
              path="pending-signatures/clinician-report/"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <ProviderReportListing></ProviderReportListing>
                </ProtectedRoute>
              }
            />
            <Route
              path="pending-signatures/progress-note-listing/:ClinicianId"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <PendingSgnatureProgressNoteListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="overall-compliance"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <SensorDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="overall-compliance/:ClinicianId/progress-note-listing"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <ClinicianProgressNoteListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="overall-compliance/:ClinicId/clinic-provider-stats"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <ClinicProviderStatsListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="overall-compliance/:RegionId/region-provider-stats"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <RegionProviderStatsListing />
                </ProtectedRoute>
              }
            />
            <Route
              path="overall-compliance/top-clinics"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <TopClinicsTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="overall-compliance/top-regions"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <TopRegionsTable />
                </ProtectedRoute>
              }
            />
            <Route
              path="overall-compliance/top-clinicians"
              element={
                <ProtectedRoute allowedRoles={["client_admin"]}>
                  <TopCliniciansTable />
                </ProtectedRoute>
              }
            />
          </Route>

          <Route path="users" element={<Outlet />}>
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <Users />
                </ProtectedRoute>
              }
            />
            <Route
              path="newuser"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <CreateNewUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="updateuser/:UserId"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <CreateNewUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="bulk/upload/:UploadStep"
              element={
                <ProtectedRoute allowedRoles={["csm"]}>
                  <UserBulkUpload />
                </ProtectedRoute>
              }
            />
            <Route
              path="bulk/upload/:UploadStep/edit/:Id"
              element={
                <ProtectedRoute allowedRoles={["csm"]}>
                  <UserBulkUploadEdit />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="files" element={<Outlet />}>
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <Files />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="user_groups" element={<Outlet />}>
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <UserGroupsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="create"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <CreateNewUserGroup />
                </ProtectedRoute>
              }
            />
            <Route
              path="edit/:GroupId"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <EditNewUserGroup />
                </ProtectedRoute>
              }
            />
            <Route
              path="bulk/upload/:UploadStep"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <UserGroupBulkUpload />
                </ProtectedRoute>
              }
            />
            <Route path="view_details/:GroupId" element={<UserGroupDetail />} />
            <Route
              path="bulk/upload/:UploadStep/edit/:Id"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <UserGroupBulkUploadEdit />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="billing" element={<Outlet />}>
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <BillingOverview />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="targets" element={<Outlet />}>
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <TargetDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports/:ReportType"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <TargetReports />
                </ProtectedRoute>
              }
            />
            <Route
              path="clinical/performance"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <ClinicalPerformance />
                </ProtectedRoute>
              }
            />
            <Route
              path="clinical/performance/:Clinic"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <ClinicLevelPerformancePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="configurations/create"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <ScorecardConfigCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="configurations/edit/:ConfigurationId"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <ScorecardConfigEdit />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="settings" element={<Settings />} />
          <Route path="reports" element={<Outlet />}>
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <Reports />
                </ProtectedRoute>
              }
            />
            <Route
              path="user_engagement"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <UserEngagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="user_tracker"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <UserTrackerReport />
                </ProtectedRoute>
              }
            />
            <Route
              path="user_compliance"
              element={
                <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                  <UserCompliance />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>
        <Route path="feed" element={<RequireAuth />}>
          <Route path="vow" element={<Outlet />}>
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <FeedVOWList />
                </ProtectedRoute>
              }
            />
            <Route
              path="create"
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <FeedVOWCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="edit/:PostId"
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <FeedVOWEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="modules/:ModuleId/preview"
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <ProgramsModulesPreview />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route
            path="assignment/:PostId"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <FeedAssignContent />
              </ProtectedRoute>
            }
          />
          <Route path="assignment" element={<Outlet />}>
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <FeedContentAssignment />
                </ProtectedRoute>
              }
            />
            <Route
              path=":AssignmentId/preview"
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <FeedContentAssignmentDetail />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="articles" element={<Outlet />}>
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <FeedArticlesList />
                </ProtectedRoute>
              }
            />
            <Route
              path="create"
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <FeedArticlesCreate />
                </ProtectedRoute>
              }
            />
            <Route
              path="edit/:PostId"
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <FeedArticlesEdit />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="pearls">
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <PearlsAndTipsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="create"
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <PearlsAndTipsCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path=":PostId/edit"
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <PearlsAndTipsEditPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route path="tips">
            <Route
              path=""
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <PearlsAndTipsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="create"
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <PearlsAndTipsCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path=":PostId/edit"
              element={
                <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                  <PearlsAndTipsEditPage />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>
        <Route path="programs" element={<RequireAuth />}>
          <Route
            path="list"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramsList />
              </ProtectedRoute>
            }
          />
          <Route
            path=":ProgramId/preview"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramPreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="assignment"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramAssignmentlist key="unique-2" />
              </ProtectedRoute>
            }
          />
          <Route
            path="assignment/:ModuleId"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramAssignment />
              </ProtectedRoute>
            }
          />
          <Route
            path="assignment/:ModuleId/preview"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <AssignmentPreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="list/create"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramsCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="list/edit/:ProgramId"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramsEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="questions"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramsQuestionsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="questions/create"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramsQuestionsCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="questions/detail/:QuestionId"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramsQuestionsDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="questions/detail/:QuestionId/associations"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramQuestionsAssociations />
              </ProtectedRoute>
            }
          />
          <Route
            path="questions/:QuestionId/edit/"
            element={
              <ProtectedRoute allowedRoles={["clinical_team"]}>
                <ProgramsQuestionsEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="questions/upload/:UploadStep"
            element={
              <ProtectedRoute allowedRoles={["clinical_team"]}>
                <ProgramsQuestionsUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path="questions/upload/:UploadStep/edit/:QuestionId"
            element={
              <ProtectedRoute allowedRoles={["clinical_team"]}>
                <ProgramsQuestionsCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="modules"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramsModulesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="modules/create"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramsModulesCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path="modules/edit/:ModuleId"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramsModulesEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="modules/:ModuleId/preview"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ProgramsModulesPreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="survey-questions"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <SurveyQuestions />
              </ProtectedRoute>
            }
          />
          <Route
            path="survey-questions/:questionId"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <SurveyQuestionDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="survey-questions/create"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <CreateSurveyQuestion mode="create" />
              </ProtectedRoute>
            }
          />
          <Route
            path="survey-questions/:questionId/edit"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <EditSurveyQuestion mode="edit" />
              </ProtectedRoute>
            }
          />
          <Route
            path="survey-modules"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <SurveyModules />
              </ProtectedRoute>
            }
          />
          <Route
            path="survey-modules/:moduleId"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <SurveyModuleDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="survey-modules/create"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <CreateSurveyModule />
              </ProtectedRoute>
            }
          />
          <Route
            path="survey-assignment"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <SurveyAssignments />
              </ProtectedRoute>
            }
          />
          <Route
            path="survey-assignment/:surveyAssignmentId"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <SurveyAssignmentDetailsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="library"
            element={
              <ProtectedRoute allowedRoles={["client_admin"]}>
                <ProgramLibrary />
              </ProtectedRoute>
            }
          />
          <Route
            path="library/:programId/details"
            element={
              <ProtectedRoute allowedRoles={["client_admin"]}>
                <ProgramDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="library/:ModuleId/assignment"
            element={
              <ProtectedRoute allowedRoles={["client_admin"]}>
                <ProgramAssignment />
              </ProtectedRoute>
            }
          />
          <Route
            path="assigned"
            element={
              <ProtectedRoute allowedRoles={["client_admin"]}>
                <ProgramAssignmentlist />
              </ProtectedRoute>
            }
          />
          <Route
            path="assigned/:ModuleId/preview"
            element={
              <ProtectedRoute allowedRoles={["client_admin"]}>
                <AssignmentPreview />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="tournaments" element={<RequireAuth />}>
          <Route
            path="create"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <TournamentCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path=":TournamentId/detail"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <TournamentDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path=":TournamentId/edit"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <TournamentEdit />
              </ProtectedRoute>
            }
          />
          <Route
            path="list"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <TournamentList />
              </ProtectedRoute>
            }
          />
          <Route
            path=":TournamentId/contest"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ContestsCreate />
              </ProtectedRoute>
            }
          />
          <Route
            path=":TournamentId/contest/:ContestId/detail"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ContestDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="contestlisting"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <Contestlisting />
              </ProtectedRoute>
            }
          />
          <Route
            path=":TournamentId/contest/:ContestId/edit"
            element={
              <ProtectedRoute allowedRoles={["clinical_team", "csm"]}>
                <ContestEdit />
              </ProtectedRoute>
            }
          />
        </Route>
        <Route path="tailored-content" element={<RequireAuth />}>
          <Route
            path="create"
            element={
              <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                <TaileredContestCreatePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="list"
            element={
              <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                <TailoredContentlisting />
              </ProtectedRoute>
            }
          />
          <Route
            path=":contentId/detail"
            element={
              <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                <TailoredContestDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path=":contentId/questionlisting/:userId"
            element={
              <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                <CustomAssignmentQuestionListing />
              </ProtectedRoute>
            }
          />
          <Route
            path="createLearningPointTailoredContent"
            element={
              <ProtectedRoute allowedRoles={["client_admin", "csm"]}>
                <LearningPointsTailoredContestCreatePage />
              </ProtectedRoute>
            }
          />
        </Route>
        {/* invalid route */}
        <Route path="404/:errorType" element={<ErrorPage />} />
        <Route path="*" element={<ErrorPage />} />
      </Route>
    </Routes>
  );
}

export default App;
